import { gql } from '@/__generated__/storefront';

/**
 * Shared Storefront API query for fetching collection products
 * Used by: Cross-sell, Gift collection, and other features
 * @param variantsLimit - Number of variants to fetch per product (default: 1 for initial display)
 */
export const collectionProductsQuery = gql(`
  query CollectionProducts($handle: String!, $country: CountryCode!, $first: Int!, $after: String, $variantsLimit: Int = 10) @inContext(country: $country) {
    collectionByHandle(handle: $handle) {
      id
      title
      products(first: $first, after: $after) {
        pageInfo {
          hasNextPage
          endCursor
        }
        nodes {
          id
          title
          handle
          availableForSale
          featuredImage {
            altText
            url
            width
            height
          }
          variants(first: $variantsLimit) {
            nodes {
              id
              price {
                amount
                currencyCode
              }
              compareAtPrice {
                amount
                currencyCode
              }
              availableForSale
              image {
                url
                altText
              }
            }
          }
        }
      }
    }
  }
`);

/**
 * Client-side fallback resolution for category-carousel blocks whose
 * server-side all_products[]/collections[] lookup came back blank (Shopify
 * caps all_products[] at 20 unique handles per page render). Mirrors the data
 * needed by snippets/product-capture-title.liquid, product-capture-subtitle.liquid,
 * product-capture-subtitle-tag.liquid and product-capture-yarn-weight-category.liquid,
 * plus the variants needed to replicate card-product-price.liquid's cheapest-variant logic.
 */
export const carouselProductByHandleQuery = gql(`
  query CarouselProductByHandle($handle: String!, $country: CountryCode!) @inContext(country: $country) {
    productByHandle(handle: $handle) {
      id
      title
      featuredImage {
        altText
        url
        width
        height
      }
      entityTypeMetafield: metafield(key: "entity_type", namespace: "hobbii") {
        value
      }
      brandMetafield: metafield(key: "brand", namespace: "hobbii") {
        value
      }
      subtitleMetafield: metafield(key: "subtitle", namespace: "hobbii") {
        value
      }
      designerMetafield: metafield(key: "designer", namespace: "hobbii") {
        value
      }
      patternKindMetafield: metafield(key: "pattern_kind", namespace: "hobbii") {
        value
      }
      yarnWeightCategoryMetafield: metafield(key: "yarn_weight_category", namespace: "hobbii") {
        value
      }
      variants(first: 250) {
        nodes {
          id
          price {
            amount
            currencyCode
          }
          compareAtPrice {
            amount
            currencyCode
          }
        }
      }
    }
  }
`);

export const carouselCollectionByHandleQuery = gql(`
  query CarouselCollectionByHandle($handle: String!, $country: CountryCode!) @inContext(country: $country) {
    collectionByHandle(handle: $handle) {
      id
      image {
        url
        altText
      }
      products(first: 1) {
        nodes {
          featuredImage {
            url
            altText
          }
        }
      }
    }
  }
`);

export const productByHandleQuery = gql(`
  query ProductByHandle($handle: String!, $country: CountryCode!) @inContext(country: $country) {
    productByHandle(handle: $handle) {
      id
      title
      handle
      featuredImage {
        altText
        url
        width
        height
      }
      variantKindMetafield: metafield(key: "variant_kind", namespace: "hobbii") {
        value
      }
      variants(first: 250) {
        nodes {
          id
          title
          price {
            amount
            currencyCode
          }
          compareAtPrice {
            amount
            currencyCode
          }
          availableForSale
          quantityAvailable
          image {
            url
            altText
          }
          disabledMetafield: metafield(key: "disabled", namespace: "hobbii") {
            value
          }
          discontinuedMetafield: metafield(key: "discontinued", namespace: "hobbii") {
            value
          }
          colorCodeMetafield: metafield(key: "color_code", namespace: "hobbii") {
            value
          }
        }
      }
    }
  }
`);
